import React, { useState } from 'react';
import { createMessage } from '../data/api';

const glassCard = {
  background: 'rgba(255,252,240,0.55)',
  backdropFilter: 'blur(16px) saturate(1.30) brightness(1.04)',
  WebkitBackdropFilter: 'blur(16px) saturate(1.30) brightness(1.04)',
  border: '1px solid rgba(200,170,60,0.45)',
  borderRadius: 20,
  boxShadow: [
    'inset 0 1.5px 0 rgba(255,255,255,0.85)',
    'inset 0 -1px 0 rgba(200,170,60,0.22)',
    '0 8px 32px rgba(140,100,20,0.12)',
    '0 0 0 0.5px rgba(200,170,60,0.20)',
  ].join(', '),
};

const inputStyle = {
  width: '100%',
  background: 'rgba(255,252,240,0.70)',
  border: '1.5px solid rgba(200,170,60,0.45)',
  borderRadius: 10,
  padding: '12px 14px',
  fontSize: 15,
  color: '#1E1A14',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: "'Cormorant Garamond', serif",
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

const labelStyle = {
  fontSize: 13,
  fontFamily: "'Cinzel', serif",
  fontWeight: 700,
  color: 'rgba(60,40,8,0.88)',
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  marginBottom: 7,
  display: 'block',
};

const GoldDivider = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
    <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(200,170,60,0.60))' }} />
    <span style={{ color: 'rgba(200,170,60,0.90)', fontSize: 10 }}>✦</span>
    <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(200,170,60,0.60), transparent)' }} />
  </div>
);

const MessageForm = () => {
  const [tab, setTab] = useState('note');
  const [form, setForm] = useState({ guest_name: '', email: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [popup, setPopup] = useState(null);

  const set = (k, v) => {
    if (k === 'content' && v.length > 500) return;
    setForm(f => ({ ...f, [k]: v }));
    setError('');
  };

  const submit = async () => {
    if (!form.guest_name.trim()) return setError('Name is required');
    if (!form.content.trim()) return setError('Message is required');
    setLoading(true);
    try {
      await createMessage({ ...form, message_type: tab });
      setPopup({
        message: tab === 'note'
          ? 'Your message has been sent! Thank you.'
          : "Your question has been submitted! We'll get back to you.",
      });
      setForm({ guest_name: '', email: '', content: '' });
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="messages" className="card-h-pad" style={{ width: '100%', paddingTop: 16, paddingBottom: 28, boxSizing: 'border-box' }}>
      <style>{`.msg-field::placeholder { color: rgba(60,40,10,0.42); }`}</style>
      <div style={{ ...glassCard, padding: '28px 22px' }}>

        {/* ── Header ── */}
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <GoldDivider />
          <p style={{
            fontFamily: "'Cinzel', serif", fontSize: 15, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '2px',
            color: 'rgba(60,40,8,0.88)', margin: '14px 0 10px',
          }}>
            Messages & Questions
          </p>
          <p style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600,
            color: '#1E1A14', margin: '0 0 8px', letterSpacing: '0.5px',
            textShadow: '0 1px 0 rgba(255,255,255,0.70)',
          }}>
            Send a Note
          </p>
          <p style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: 17,
            color: 'rgba(55,38,8,0.82)', lineHeight: 1.6, margin: 0, fontStyle: 'italic',
          }}>
            Leave a personal message for Carol or ask any questions you may have.
          </p>
        </div>

        {/* ── Tab Switcher ── */}
        <div style={{
          background: 'rgba(255,252,240,0.50)',
          border: '1px solid rgba(200,170,60,0.35)',
          borderRadius: 12,
          display: 'flex',
          padding: 4,
          marginBottom: 20,
        }}>
          {['note', 'question'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 8,
                border: tab === t ? '1px solid rgba(200,170,60,0.65)' : '1px solid transparent',
                cursor: 'pointer',
                fontSize: 12,
                fontFamily: "'Cinzel', serif",
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                background: tab === t
                  ? 'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 100%)'
                  : 'transparent',
                color: tab === t ? '#D4A853' : 'rgba(60,40,8,0.80)',
                fontWeight: tab === t ? 700 : 400,
                transition: 'all 0.25s ease',
                boxShadow: tab === t ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
              }}
            >
              {t === 'note' ? 'Send a Note' : 'Ask a Question'}
            </button>
          ))}
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{
            background: 'rgba(255,240,240,0.85)',
            border: '1px solid rgba(220,100,100,0.40)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: '#C0392B',
            fontFamily: "'Cormorant Garamond', serif",
          }}>
            {error}
          </div>
        )}

        {/* ── Fields ── */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>
            Your Name <span style={{ color: '#C0392B' }}>*</span>
          </label>
          <input
            style={inputStyle}
            className="msg-field"
            placeholder="Your name"
            value={form.guest_name}
            onChange={e => set('guest_name', e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Email Address</label>
          <input
            style={inputStyle}
            className="msg-field"
            placeholder="your@email.com"
            type="email"
            value={form.email}
            onChange={e => set('email', e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>
            Your Message <span style={{ color: '#C0392B' }}>*</span>
          </label>
          <textarea
            style={{ ...inputStyle, minHeight: 110, resize: 'vertical' }}
            className="msg-field"
            placeholder={
              tab === 'note'
                ? 'Share a memory or message for Carol...'
                : 'What would you like to know?'
            }
            value={form.content}
            onChange={e => set('content', e.target.value)}
          />
          <div style={{
            fontSize: 12, marginTop: 6,
            color: form.content.length >= 480 ? '#C0392B' : form.content.length >= 400 ? '#C49830' : 'rgba(60,40,8,0.72)',
            textAlign: 'right', fontFamily: "'Cinzel', serif", letterSpacing: '0.5px',
            fontWeight: form.content.length >= 400 ? 700 : 500,
            transition: 'color 0.3s ease',
          }}>
            {form.content.length} / 500
          </div>
        </div>

        {/* ── Gold Divider ── */}
        <div style={{ marginBottom: 18 }}>
          <GoldDivider />
        </div>

        {/* ── Submit Button ── */}
        <button
          onClick={submit}
          disabled={loading}
          data-testid="message-form-submit"
          style={{
            width: '100%',
            background: 'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)',
            color: '#D4A853',
            border: '1px solid rgba(200,170,60,0.65)',
            borderRadius: 12,
            padding: '16px 22px',
            fontSize: 16,
            fontFamily: "'Cinzel', serif",
            fontWeight: 700,
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.25s ease',
            boxShadow: '0 4px 16px rgba(0,0,0,0.18), inset 0 1px 0 rgba(212,168,83,0.15)',
          }}
        >
          {loading ? 'Sending…' : 'Send Message'}
        </button>

      </div>

      {/* ── Success Popup ── */}
      {popup && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(30,26,20,0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{ ...glassCard, padding: '36px 28px', maxWidth: 340, width: '88%', textAlign: 'center' }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: 'rgba(200,170,60,0.12)',
              border: '1px solid rgba(200,170,60,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px',
              fontSize: 24, color: 'rgba(180,140,30,0.90)',
            }}>
              ✓
            </div>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 17, color: '#1E1A14', lineHeight: 1.65,
              marginBottom: 24, fontStyle: 'italic',
            }}>
              {popup.message}
            </p>
            <button
              onClick={() => setPopup(null)}
              style={{
                background: 'linear-gradient(180deg, #1A1A1A 0%, #000000 100%)',
                color: '#D4A853',
                border: '1px solid rgba(200,170,60,0.65)',
                borderRadius: 10,
                padding: '11px 32px',
                fontSize: 10,
                fontFamily: "'Cinzel', serif",
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Thank You
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageForm;
